import type { Topic } from "./types";

export const PREPOSITIONS: Topic = {
  slug: "voorzetsels",
  title: "Voorzetsels",
  tagline: "A2 prepositions — place, direction, time, and fixed combinations.",
  duration: "12 min read",
  summary:
    "The small words that glue Dutch sentences together. Place (in, op, aan), direction (naar, uit), time (om, op, in), and the fixed verb-preposition pairs DUO loves to test.",
  relevantDeelen: [1, 2, 3, 4],
  relevantCriteria: ["wo", "gr"],
  sections: [
    {
      kind: "heading",
      level: 2,
      text: "What is a voorzetsel?",
    },
    {
      kind: "paragraph",
      text: "A voorzetsel (preposition) is a small word that links a noun to the rest of the sentence. In Dutch, prepositions sit BEFORE the noun, the same as English. The hard part is which one to pick — Dutch and English don't always match. \"At school\" is op school, not aan school. \"On holiday\" is op vakantie, not in vakantie.",
    },
    {
      kind: "callout",
      tone: "rule",
      title: "Rule of thumb",
      text: "Don't translate the preposition word-by-word from English. Memorise the Dutch combination as a unit (op school, naar huis, met de bus).",
    },

    { kind: "heading", level: 2, text: "Plaats — Where things ARE" },
    {
      kind: "paragraph",
      text: "Place prepositions describe a static location. The verb is usually staan, liggen, zitten, hangen, or zijn.",
    },
    {
      kind: "table",
      headers: ["Dutch", "English", "Example"],
      emphasizeFirstCol: true,
      rows: [
        ["in", "in", "Het boek ligt in de tas."],
        ["op", "on (a surface)", "De kop staat op de tafel."],
        ["aan", "on (a wall) / at", "Het schilderij hangt aan de muur."],
        ["achter", "behind", "De auto staat achter het huis."],
        ["voor", "in front of", "De hond zit voor de deur."],
        ["naast", "next to", "De bank staat naast het raam."],
        ["tussen", "between", "Het kind staat tussen de ouders."],
        ["onder", "under", "De kat slaapt onder het bed."],
        ["boven", "above", "De lamp hangt boven de tafel."],
        ["bij", "with / near", "Ik woon bij mijn ouders."],
      ],
    },
    { kind: "heading", level: 3, text: "Common A2 place expressions" },
    {
      kind: "paragraph",
      text: "These are the patterns DUO graders expect. Each is a fixed unit — you can't swap the preposition.",
    },
    {
      kind: "table",
      headers: ["Dutch expression", "Meaning"],
      emphasizeFirstCol: true,
      rows: [
        ["op school", "at school"],
        ["op het werk / op werk", "at work"],
        ["op vakantie", "on holiday"],
        ["op de markt", "at the market"],
        ["in het ziekenhuis", "in (the) hospital"],
        ["in de winkel", "in the shop"],
        ["bij de huisarts", "at the GP"],
        ["bij de bakker", "at the baker"],
        ["thuis", "at home (no preposition)"],
        ["op straat", "on the street"],
      ],
    },
    {
      kind: "callout",
      tone: "warning",
      title: "Watch out",
      text: "\"At home\" is just thuis — no preposition, no article. Saying *in huis* or *op huis* will make a Dutch speaker pause.",
    },

    { kind: "heading", level: 2, text: "Richting — Where things GO" },
    {
      kind: "paragraph",
      text: "Direction prepositions describe motion toward, from, or along a place. The verb is usually gaan, komen, lopen, fietsen, rijden, or reizen.",
    },
    {
      kind: "table",
      headers: ["Dutch", "English", "Example"],
      emphasizeFirstCol: true,
      rows: [
        ["naar", "to", "Ik ga naar Amsterdam."],
        ["uit", "out of, from", "Hij komt uit Turkije."],
        ["van", "from", "De brief is van mijn moeder."],
        ["tot", "until, up to", "Ik werk tot vijf uur."],
        ["vanaf", "from (a place / time)", "Ik werk vanaf negen uur."],
        ["langs", "along, past", "We lopen langs de gracht."],
        ["door", "through", "We rijden door de stad."],
        ["over", "across, over", "We lopen over de brug."],
      ],
    },
    { kind: "heading", level: 3, text: "Vehicle prepositions" },
    {
      kind: "paragraph",
      text: "Dutch uses met for most vehicles, op only for things you sit ON like a bike or horse, and te voet for walking. This is one of the most-tested A2 patterns.",
    },
    {
      kind: "table",
      headers: ["Vehicle", "Dutch"],
      emphasizeFirstCol: true,
      rows: [
        ["car", "met de auto"],
        ["bus", "met de bus"],
        ["train", "met de trein"],
        ["plane", "met het vliegtuig"],
        ["bike", "op de fiets / met de fiets"],
        ["scooter", "op de scooter"],
        ["walking", "te voet / lopend"],
      ],
    },
    {
      kind: "callout",
      tone: "tip",
      title: "Memory aid",
      text: "If you sit ON it, use op (op de fiets). If you sit IN it, use met (met de auto).",
    },

    { kind: "heading", level: 2, text: "Tijd — When" },
    {
      kind: "paragraph",
      text: "Time prepositions are mostly about which scale of time you're naming: a clock time, a day, a month, a year. Pick the right one for the scale.",
    },
    {
      kind: "table",
      headers: ["Dutch", "Use for", "Example"],
      emphasizeFirstCol: true,
      rows: [
        ["om", "clock time", "Ik kom om acht uur."],
        ["op", "day / date", "Op maandag werk ik niet."],
        ["in", "month / year / season", "In juli ga ik op vakantie."],
        ["voor", "before", "Ik ben er voor zes uur."],
        ["na", "after", "Na het werk ga ik sporten."],
        ["sinds", "since (a point in the past, still ongoing)", "Ik woon hier sinds 2020."],
        ["vanaf", "from a starting time onward", "De winkel is open vanaf negen uur."],
        ["tijdens", "during", "Tijdens de les mag je niet eten."],
        ["over", "in (future) / about", "Over een week ga ik weg."],
        ["tot", "until", "Ik blijf tot vijf uur."],
      ],
    },
    {
      kind: "callout",
      tone: "rule",
      title: "Quick decision",
      text: "Clock time → om · Day → op · Month/year/season → in. If you remember nothing else, remember this triplet.",
    },

    { kind: "heading", level: 2, text: "Vaste voorzetsels — Fixed combinations" },
    {
      kind: "paragraph",
      text: "Some Dutch verbs always come with a specific preposition. The combination is fixed — using a different preposition is wrong, even when the English equivalent uses something else. Learn them as one unit, not as separate words.",
    },
    {
      kind: "table",
      headers: ["Verb + prep", "Example"],
      emphasizeFirstCol: true,
      rows: [
        ["luisteren naar", "Ik luister naar muziek."],
        ["kijken naar", "We kijken naar de tv."],
        ["denken aan", "Ik denk aan mijn familie."],
        ["wachten op", "Ik wacht op de bus."],
        ["beginnen met", "We beginnen met de les."],
        ["stoppen met", "Hij stopt met roken."],
        ["spreken / praten met", "Ik spreek met mijn collega."],
        ["praten over", "Ze praten over het weer."],
        ["werken bij", "Zij werkt bij Albert Heijn."],
        ["houden van", "Ik houd van koffie."],
        ["bang zijn voor", "Ik ben bang voor honden."],
        ["blij zijn met", "Ik ben blij met mijn cadeau."],
        ["trouwen met", "Hij trouwt met Sara."],
        ["zoeken naar", "We zoeken naar onze sleutels."],
      ],
    },
    {
      kind: "callout",
      tone: "warning",
      title: "Common A2 trap",
      text: "Don't say *luisteren aan* or *wachten voor* — those translate the English preposition word-by-word. The Dutch combinations are luisteren naar and wachten op. Always.",
    },

    { kind: "heading", level: 2, text: "Putting it together" },
    {
      kind: "paragraph",
      text: "Below are the kinds of full sentences DUO Spreken expects. Read them aloud once each — the rhythm of the prepositions is what your ear needs to learn.",
    },
    {
      kind: "examples",
      rows: [
        { dutch: "Op zaterdag ga ik met de fiets naar de markt.", english: "On Saturday I cycle to the market." },
        { dutch: "Ik woon sinds 2020 in Nederland, in Rotterdam.", english: "I've lived in the Netherlands since 2020, in Rotterdam." },
        { dutch: "Tijdens het werk luister ik naar Nederlandse podcasts.", english: "During work I listen to Dutch podcasts." },
        { dutch: "Mijn vriend wacht op mij voor de winkel.", english: "My friend is waiting for me in front of the shop." },
        { dutch: "Om zes uur eet ik thuis met mijn familie.", english: "At six o'clock I eat at home with my family." },
        { dutch: "Na de les ga ik naar de huisarts.", english: "After class I go to the GP." },
      ],
    },

    { kind: "heading", level: 2, text: "Sentence formulae you can reuse" },
    {
      kind: "paragraph",
      text: "These are templates you can build sentences from on the spot. Memorise the pattern, slot in your own nouns, and you'll never freeze on \"how do I say where I'm going?\".",
    },
    {
      kind: "formula",
      title: "Where you live / are",
      pattern: "Ik woon / ben + in + [stad of land]",
      legend: [
        { token: "[stad of land]", meaning: "city or country (no article)" },
      ],
      examples: [
        { dutch: "Ik woon in Rotterdam.", english: "I live in Rotterdam." },
        { dutch: "Ik ben in Nederland sinds 2020.", english: "I've been in the Netherlands since 2020." },
      ],
    },
    {
      kind: "formula",
      title: "Where you're going",
      pattern: "Ik ga + naar + [bestemming]",
      legend: [
        { token: "[bestemming]", meaning: "destination — a place, building, person" },
      ],
      examples: [
        { dutch: "Ik ga naar de markt.", english: "I'm going to the market." },
        { dutch: "Ik ga naar mijn moeder.", english: "I'm going to my mother's." },
        { dutch: "Ik ga naar Amsterdam met de trein.", english: "I'm going to Amsterdam by train." },
      ],
    },
    {
      kind: "formula",
      title: "How you travel",
      pattern: "Ik ga + met + de/het + [voertuig]   —   Ik ga + op + de + [fiets / scooter]",
      legend: [
        { token: "[voertuig]", meaning: "auto, bus, trein, vliegtuig, …" },
      ],
      examples: [
        { dutch: "Ik ga met de bus naar werk.", english: "I take the bus to work." },
        { dutch: "Ik ga op de fiets naar school.", english: "I cycle to school." },
      ],
    },
    {
      kind: "formula",
      title: "When something happens",
      pattern: "[onderwerp] + [werkwoord] + om + [tijd]   —   op + [dag]   —   in + [maand / jaar]",
      examples: [
        { dutch: "Ik begin om negen uur.", english: "I start at nine." },
        { dutch: "Op zaterdag werk ik niet.", english: "On Saturday I don't work." },
        { dutch: "In juli ga ik op vakantie.", english: "In July I go on holiday." },
      ],
    },
    {
      kind: "formula",
      title: "Verb + fixed preposition",
      pattern: "[onderwerp] + [werkwoord] + [vaste voorzetsel] + [object]",
      legend: [
        { token: "[werkwoord] + [voorzetsel]", meaning: "luisteren naar, wachten op, denken aan, houden van, kijken naar, beginnen met" },
      ],
      examples: [
        { dutch: "Ik luister naar Nederlandse muziek.", english: "I listen to Dutch music." },
        { dutch: "We wachten op de bus.", english: "We're waiting for the bus." },
        { dutch: "Hij houdt van koffie.", english: "He likes coffee." },
      ],
    },

    { kind: "heading", level: 2, text: "Quick checklist before the exam" },
    {
      kind: "list",
      items: [
        "Clock time? om. Day? op. Month or year? in.",
        "On a bike or scooter? op. In a car/bus/train? met.",
        "At home is just thuis — no preposition.",
        "Memorise verb-pairs: luisteren naar, wachten op, denken aan, houden van.",
        "If you can't recall, pick op or met — they fit more A2 contexts than any other.",
      ],
    },
  ],
};
